/**
 * @file Handle subscription to everything related to messages
 */

import { UpdateResolver } from '@urql/exchange-graphcache';

import {
  ActionType,
  MessageInfoSubscription,
  MessageInfoSubscriptionVariables,
  MessagesDocument,
  Node,
} from '../../../../../../generated/graphql';
import paginatedQueryCreation from '../utils/updaters/paginatedQueryCreation';
import paginatedQueryDeletion from '../utils/updaters/paginatedQueryDeletion';
import {
  addNodeToPaginatedQuery,
  filterQueries,
  removeNodeFromPaginatedQuery,
} from '../utils/updaters/paginatedQueryHelpers';

const queryName = 'messages';

/**
 * Handle subscription to everything related to messages
 *
 * @param result                     Result of a graphql operation
 * @param result.messageInfo         Event data
 * @param result.messageInfo.action  The action that triggered the event
 * @param result.messageInfo.message The updated message
 * @param args                       Arguments of a graphql operation
 * @param args.topicId               The ID of the topic the message is in
 * @param cache                      URQL graphql cache
 */
const messageInfoSubscription: UpdateResolver<
  MessageInfoSubscription,
  MessageInfoSubscriptionVariables
> = ({ messageInfo: { action, message } }, { topicId }, cache) => {
  if (action === ActionType.Created || action === ActionType.Unarchived) {
    paginatedQueryCreation({
      cache,
      filterQueries: filterQueries({
        filterById: topicId,
        parentIdName: 'topicId',
        queryName,
      }),
      updateQuery: variables => {
        addNodeToPaginatedQuery({
          cache,
          node: message as Node,
          query: MessagesDocument,
          queryName,
          variables,
        });
      },
    });
    return;
  }

  if (action === ActionType.Archived || action === ActionType.Deleted) {
    paginatedQueryDeletion({
      cache,
      queryName,
      updateQuery: variables => {
        removeNodeFromPaginatedQuery({
          cache,
          nodeId: message.id,
          query: MessagesDocument,
          queryName,
          variables,
        });
      },
    });
    return;
  }
};

export default messageInfoSubscription;
